import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  ShieldCheck,
  Building2,
  Coins,
  Globe2,
  Layers,
  PanelLeftClose,
  PanelLeftOpen,
  ChevronDown,
  Settings,
  UserPlus,
  Briefcase,
  Contact,
  GitBranch,
  SlidersHorizontal,
  Sliders,
  Package,
  Boxes,
  PackagePlus,
  Warehouse,
  Truck,
  Factory,
  Workflow,
  Handshake,
  FlaskConical,
  PackageCheck,
  MessageSquareWarning,
  FileText,
  CheckSquare,
  BarChart3,
  ClipboardCheck,
  Target,
  Kanban,
  Mail,
  Radio,
  CalendarDays,
} from "lucide-react";
import { WhatsAppIcon } from "@/components/whatsapp/WhatsAppIcon";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { api, unwrapObject } from "@/lib/api";
import { unwrapPage } from "@/lib/paging";

interface SidebarProps {
  collapsed?: boolean;
  onToggle?: () => void;
}


function CollapsedTooltip({ label, active, children }: { label: string; active: boolean; children: React.ReactNode }) {
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  if (!active) return <>{children}</>;

  return (
    <div
      onMouseEnter={(e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        setCoords({
          top: rect.top + rect.height / 2,
          left: rect.right + 10,
        });
      }}
      onMouseLeave={() => setCoords(null)}
      className="w-full flex items-center justify-center relative"
    >
      {children}
      {coords &&
        createPortal(
          <div
            style={{
              position: "fixed",
              top: coords.top,
              left: coords.left,
              transform: "translateY(-50%)",
              zIndex: 99999,
            }}
            className="pointer-events-none px-2.5 py-1 rounded-md bg-[#1f2937] text-white text-xs font-semibold shadow-2xl border border-slate-700 whitespace-nowrap animate-in fade-in zoom-in-95 duration-100 flex items-center gap-1.5"
          >
            {label}
          </div>,
          document.body
        )}
    </div>
  );
}

/** A titled group of sidebar links; hidden when the user may see none of them. Collapsed, it is a thin divider. */
function NavSection({ label, collapsed, show, children }: { label: string; collapsed: boolean; show: boolean; children: React.ReactNode }) {
  if (!show) return null;
  return (
    <div className="space-y-1.5 pb-1">
      {collapsed ? (
        <div className="w-8 h-px bg-slate-700 mx-auto my-2" />
      ) : (
        <div className="px-2.5 pt-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-white/80">{label}</div>
      )}
      {children}
    </div>
  );
}

export function Sidebar({ collapsed = false, onToggle }: SidebarProps) {
  const location = useLocation();
  const { hasPermission, user } = useAuth();
  const isSuperAdmin = String(user?.UserRole?.RoleCode || "").toUpperCase() === "SUPER_ADMIN";

  const canReadLeads = hasPermission("lead.read");
  const canReadDeals = hasPermission("deal.read");
  const canReadAccounts = hasPermission("account.read");
  const canReadContacts = hasPermission("contact.read");
  const canViewReports = hasPermission("report.view_own");
  const canReadTargets = hasPermission("target.read");
  const canReadProducts = hasPermission("product.read");
  const canReadAgents = hasPermission("agent.read");
  const canReadSamples = hasPermission("sample.read");
  const canReadOrders = hasPermission("order.read");
  const canReadComplaints = hasPermission("complaint.read");
  const canReadInventory = hasPermission("inventory.read");
  const canReadProduction = hasPermission("production.read");
  const canReadQuotes = hasPermission("quote.read");
  const canApproveQuotes = hasPermission("quote.approve");

  // Number of quotes waiting for this user's approval, refreshed on navigation
  const [pendingApprovals, setPendingApprovals] = useState(0);
  useEffect(() => {
    if (!canApproveQuotes) return;
    api
      .get("/api/Quotation/List?mine_to_approve=true&page_size=1")
      .then((res) => setPendingApprovals(unwrapPage(res).total))
      .catch(() => setPendingApprovals(0));
  }, [canApproveQuotes, location.pathname]);

  const canReadActivities = hasPermission("activity.read");
  const canReadCalendar = hasPermission("calendar.read") || canReadActivities;

  // Number of overdue + today tasks for this user
  const [activeTaskCount, setActiveTaskCount] = useState(0);

  useEffect(() => {
    if (!canReadActivities) return;
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    const fromStr = encodeURIComponent(start.toISOString());
    const toStr = encodeURIComponent(end.toISOString());

    // Tasks summary
    api
      .get(`/api/Task/Summary?from=${fromStr}&to=${toStr}&scope=mine`)
      .then((res) => {
        const data = unwrapObject<{ overdue: number; today: number }>(res);
        setActiveTaskCount((data.overdue || 0) + (data.today || 0));
      })
      .catch(() => setActiveTaskCount(0));
  }, [canReadActivities, location.pathname]);

  // Unread customer replies + emails waiting to be linked to a deal
  const canReadEmails = hasPermission("email.read");
  const [emailBadge, setEmailBadge] = useState(0);
  useEffect(() => {
    if (!canReadEmails) return;
    api
      .get("/api/DealEmail/Counts")
      .then((res) => {
        const data = unwrapObject<{ unread: number; unlinked: number }>(res);
        setEmailBadge((data.unread || 0) + (data.unlinked || 0));
      })
      .catch(() => setEmailBadge(0));
  }, [canReadEmails, location.pathname, location.search]);

  // Unread WhatsApp chats (mine + unassigned)
  const canReadWhatsApp = hasPermission("whatsapp.read");
  const [waBadge, setWaBadge] = useState(0);
  useEffect(() => {
    if (!canReadWhatsApp) return;
    const loadCounts = () =>
      api
        .get("/api/WhatsApp/Counts")
        .then((res) => setWaBadge(unwrapObject<{ unread: number }>(res).unread || 0))
        .catch(() => setWaBadge(0));
    loadCounts();
    const timer = setInterval(loadCounts, 30000);
    return () => clearInterval(timer);
  }, [canReadWhatsApp, location.pathname, location.search]);

  const canManageUsers = hasPermission("user.manage");
  const canManageRoles = hasPermission("role.manage");
  const canSeeUserManagement = canManageUsers || canManageRoles;

  const canManageSettings = hasPermission("setting.manage");
  const canSeeWhatsAppSettings = hasPermission("whatsapp.admin");
  const canManageLeadSources = hasPermission("lead.source_manage") || isSuperAdmin;
  const canSeeSettings = canManageSettings || canSeeWhatsAppSettings || canManageLeadSources;

  const isUsersActive =
    location.pathname === "/users" || location.pathname === "/roles";
  const isSettingsActive = location.pathname.startsWith("/settings");

  type SubmenuKey = "users" | "settings" | null;

  const [openMenu, setOpenMenu] = useState<SubmenuKey>(() => {
    if (isUsersActive) return "users";
    if (isSettingsActive) return "settings";
    return null;
  });

  useEffect(() => {
    if (isUsersActive) {
      setOpenMenu("users");
    } else if (isSettingsActive) {
      setOpenMenu("settings");
    }
  }, [location.pathname, isUsersActive, isSettingsActive]);

  // Exclusive accordion: opening one closes any previously open submenu
  const toggleMenu = (key: "users" | "settings") => {
    setOpenMenu((prev) => (prev === key ? null : key));
  };

  return (
    <aside
      className={cn(
        "shrink-0 bg-[#363636] text-sidebar-foreground flex flex-col h-full select-none transition-[width] duration-300 ease-in-out relative overflow-hidden",
        collapsed ? "w-16" : "w-56"
      )}
    >
      {/* Navigation */}
      <div className="flex-1 py-2 px-2.5 space-y-0.5 overflow-y-auto overflow-x-hidden [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
        <NavSection label="Overview" collapsed={collapsed} show={true}>
        {/* 1. Dashboard (Direct Link) */}
        <CollapsedTooltip label={"Dashboard"} active={collapsed}>
          <NavLink
          to="/dashboard"
          className={({ isActive }) =>
            cn(
              "flex items-center rounded-lg text-xs font-medium transition-all group relative",
              collapsed
                ? "justify-center size-9 mx-auto"
                : "gap-3 px-3 py-2.5",
              isActive
                ? "bg-white/20 text-white shadow-xs font-semibold"
                : "text-white hover:bg-white/10 font-medium"
            )
          }
        >
          <LayoutDashboard className={cn("shrink-0", collapsed ? "size-4" : "size-3.5")} />
          {!collapsed && (
            <span className="whitespace-nowrap overflow-hidden text-ellipsis">
              Dashboard
            </span>
          )}
        </NavLink>
        </CollapsedTooltip>

        {/* Calendar (Direct Link) */}
        {canReadCalendar && (
          <CollapsedTooltip label={"Calendar"} active={collapsed}>
            <NavLink
            to="/calendar"
            className={({ isActive }) =>
              cn(
                "flex items-center rounded-lg text-xs font-medium transition-all group relative",
                collapsed
                  ? "justify-center size-9 mx-auto"
                  : "gap-3 px-3 py-2.5",
                isActive
                  ? "bg-white/20 text-white shadow-xs font-semibold"
                  : "text-white hover:bg-white/10 font-medium"
              )
            }
          >
            <CalendarDays className={cn("shrink-0", collapsed ? "size-4" : "size-3.5")} />
            {!collapsed && (
              <span className="whitespace-nowrap overflow-hidden text-ellipsis flex-1">
                Calendar
              </span>
            )}
          </NavLink>
          </CollapsedTooltip>
        )}

        {/* Tasks (Direct Link) */}
        {canReadActivities && (
          <CollapsedTooltip label={`Tasks${activeTaskCount ? ` (${activeTaskCount})` : ""}`} active={collapsed}>
            <NavLink
            to="/tasks"
            className={({ isActive }) =>
              cn(
                "flex items-center rounded-lg text-xs font-medium transition-all group relative",
                collapsed
                  ? "justify-center size-9 mx-auto"
                  : "gap-3 px-3 py-2.5",
                isActive
                  ? "bg-white/20 text-white shadow-xs font-semibold"
                  : "text-white hover:bg-white/10 font-medium"
              )
            }
          >
            <CheckSquare className={cn("shrink-0", collapsed ? "size-4" : "size-3.5")} />
            {!collapsed && (
              <span className="whitespace-nowrap overflow-hidden text-ellipsis flex-1">
                Tasks
              </span>
            )}
            {activeTaskCount > 0 &&
              (collapsed ? (
                <span className="absolute top-1.5 right-1.5 size-2 rounded-full bg-primary" />
              ) : (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-primary text-primary-foreground">
                  {activeTaskCount}
                </span>
              ))}
          </NavLink>
          </CollapsedTooltip>
        )}
        </NavSection>

        <NavSection label="Communication" collapsed={collapsed} show={canReadEmails || canReadWhatsApp}>
        {/* Emails (Direct Link) */}
        {canReadEmails && (
          <CollapsedTooltip label={`Emails${emailBadge ? ` (${emailBadge})` : ""}`} active={collapsed}>
            <NavLink
            to="/emails"
            className={({ isActive }) =>
              cn(
                "flex items-center rounded-lg text-xs font-medium transition-all group relative",
                collapsed
                  ? "justify-center size-9 mx-auto"
                  : "gap-3 px-3 py-2.5",
                isActive
                  ? "bg-white/20 text-white shadow-xs font-semibold"
                  : "text-white hover:bg-white/10 font-medium"
              )
            }
          >
            <Mail className={cn("shrink-0", collapsed ? "size-4" : "size-3.5")} />
            {!collapsed && (
              <span className="whitespace-nowrap overflow-hidden text-ellipsis flex-1">
                Emails
              </span>
            )}
            {emailBadge > 0 &&
              (collapsed ? (
                <span className="absolute top-1.5 right-1.5 size-2 rounded-full bg-primary" />
              ) : (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-primary text-primary-foreground">
                  {emailBadge}
                </span>
              ))}
          </NavLink>
          </CollapsedTooltip>
        )}

        {/* WhatsApp (Direct Link) */}
        {canReadWhatsApp && (
          <CollapsedTooltip label={`WhatsApp${waBadge ? ` (${waBadge})` : ""}`} active={collapsed}>
            <NavLink
            to="/whatsapp"
            className={({ isActive }) =>
              cn(
                "flex items-center rounded-lg text-xs font-medium transition-all group relative",
                collapsed
                  ? "justify-center size-9 mx-auto"
                  : "gap-3 px-3 py-2.5",
                isActive
                  ? "bg-white/20 text-white shadow-xs font-semibold"
                  : "text-white hover:bg-white/10 font-medium"
              )
            }
          >
            <WhatsAppIcon color="#ffffff" className={cn("shrink-0", collapsed ? "size-4" : "size-3.5")} />
            {!collapsed && (
              <span className="whitespace-nowrap overflow-hidden text-ellipsis flex-1">
                WhatsApp
              </span>
            )}
            {waBadge > 0 &&
              (collapsed ? (
                <span className="absolute top-1.5 right-1.5 size-2 rounded-full bg-[#25D366]" />
              ) : (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[#25D366] text-white">
                  {waBadge}
                </span>
              ))}
          </NavLink>
          </CollapsedTooltip>
        )}
        </NavSection>

        <NavSection label="Sales" collapsed={collapsed} show={canReadLeads || canReadDeals || canReadSamples || canReadOrders || canReadQuotes}>
        {/* Leads (Direct Link) */}
        {canReadLeads && (
          <CollapsedTooltip label={"Leads"} active={collapsed}>
            <NavLink
            to="/leads"
            className={({ isActive }) =>
              cn(
                "flex items-center rounded-lg text-xs font-medium transition-all group relative",
                collapsed
                  ? "justify-center size-9 mx-auto"
                  : "gap-3 px-3 py-2.5",
                isActive
                  ? "bg-white/20 text-white shadow-xs font-semibold"
                  : "text-white hover:bg-white/10 font-medium"
              )
            }
          >
            <UserPlus className={cn("shrink-0", collapsed ? "size-4" : "size-3.5")} />
            {!collapsed && (
              <span className="whitespace-nowrap overflow-hidden text-ellipsis">
                Leads
              </span>
            )}
          </NavLink>
          </CollapsedTooltip>
        )}

        {/* Sales Pipeline (Direct Link) */}
        {canReadDeals && (
          <CollapsedTooltip label={"Sales Pipeline"} active={collapsed}>
            <NavLink
            to="/deals"
            className={({ isActive }) =>
              cn(
                "flex items-center rounded-lg text-xs font-medium transition-all group relative",
                collapsed
                  ? "justify-center size-9 mx-auto"
                  : "gap-3 px-3 py-2.5",
                isActive
                  ? "bg-white/20 text-white shadow-xs font-semibold"
                  : "text-white hover:bg-white/10 font-medium"
              )
            }
          >
            <Kanban className={cn("shrink-0", collapsed ? "size-4" : "size-3.5")} />
            {!collapsed && (
              <span className="whitespace-nowrap overflow-hidden text-ellipsis">
                Sales Pipeline
              </span>
            )}
          </NavLink>
          </CollapsedTooltip>
        )}

        {/* Samples (Direct Link) */}
        {canReadSamples && (
          <CollapsedTooltip label={"Samples"} active={collapsed}>
            <NavLink
            to="/samples"
            className={({ isActive }) =>
              cn(
                "flex items-center rounded-lg text-xs font-medium transition-all group relative",
                collapsed
                  ? "justify-center size-9 mx-auto"
                  : "gap-3 px-3 py-2.5",
                isActive
                  ? "bg-white/20 text-white shadow-xs font-semibold"
                  : "text-white hover:bg-white/10 font-medium"
              )
            }
          >
            <FlaskConical className={cn("shrink-0", collapsed ? "size-4" : "size-3.5")} />
            {!collapsed && (
              <span className="whitespace-nowrap overflow-hidden text-ellipsis">
                Samples
              </span>
            )}
          </NavLink>
          </CollapsedTooltip>
        )}

        {/* Orders (Direct Link) */}
        {canReadOrders && (
          <CollapsedTooltip label={"Orders"} active={collapsed}>
            <NavLink
            to="/orders"
            className={({ isActive }) =>
              cn(
                "flex items-center rounded-lg text-xs font-medium transition-all group relative",
                collapsed
                  ? "justify-center size-9 mx-auto"
                  : "gap-3 px-3 py-2.5",
                isActive
                  ? "bg-white/20 text-white shadow-xs font-semibold"
                  : "text-white hover:bg-white/10 font-medium"
              )
            }
          >
            <PackageCheck className={cn("shrink-0", collapsed ? "size-4" : "size-3.5")} />
            {!collapsed && (
              <span className="whitespace-nowrap overflow-hidden text-ellipsis">
                Orders & Dispatch
              </span>
            )}
          </NavLink>
          </CollapsedTooltip>
        )}

        {/* Quotes (Direct Link) */}
        {canReadQuotes && (
          <CollapsedTooltip label={`Quotes${pendingApprovals ? ` (${pendingApprovals} to approve)` : ""}`} active={collapsed}>
            <NavLink
            to="/quotes"
            className={({ isActive }) =>
              cn(
                "flex items-center rounded-lg text-xs font-medium transition-all group relative",
                collapsed
                  ? "justify-center size-9 mx-auto"
                  : "gap-3 px-3 py-2.5",
                isActive
                  ? "bg-white/20 text-white shadow-xs font-semibold"
                  : "text-white hover:bg-white/10 font-medium"
              )
            }
          >
            <FileText className={cn("shrink-0", collapsed ? "size-4" : "size-3.5")} />
            {!collapsed && (
              <span className="whitespace-nowrap overflow-hidden text-ellipsis flex-1">
                Quotes
              </span>
            )}
            {pendingApprovals > 0 &&
              (collapsed ? (
                <span className="absolute top-1.5 right-1.5 size-2 rounded-full bg-amber-400" />
              ) : (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-400 text-amber-950">
                  {pendingApprovals}
                </span>
              ))}
          </NavLink>
          </CollapsedTooltip>
        )}

        </NavSection>

        <NavSection label="Buyers" collapsed={collapsed} show={canReadAccounts || canReadContacts || canReadAgents || canReadComplaints}>
        {/* Accounts (Direct Link) */}
        {canReadAccounts && (
          <CollapsedTooltip label={"Accounts"} active={collapsed}>
            <NavLink
            to="/accounts"
            className={({ isActive }) =>
              cn(
                "flex items-center rounded-lg text-xs font-medium transition-all group relative",
                collapsed
                  ? "justify-center size-9 mx-auto"
                  : "gap-3 px-3 py-2.5",
                isActive
                  ? "bg-white/20 text-white shadow-xs font-semibold"
                  : "text-white hover:bg-white/10 font-medium"
              )
            }
          >
            <Building2 className={cn("shrink-0", collapsed ? "size-4" : "size-3.5")} />
            {!collapsed && (
              <span className="whitespace-nowrap overflow-hidden text-ellipsis">
                Accounts
              </span>
            )}
          </NavLink>
          </CollapsedTooltip>
        )}

        {/* Complaints (Direct Link) */}
        {canReadComplaints && (
          <CollapsedTooltip label={"Complaints"} active={collapsed}>
            <NavLink
            to="/complaints"
            className={({ isActive }) =>
              cn(
                "flex items-center rounded-lg text-xs font-medium transition-all group relative",
                collapsed
                  ? "justify-center size-9 mx-auto"
                  : "gap-3 px-3 py-2.5",
                isActive
                  ? "bg-white/20 text-white shadow-xs font-semibold"
                  : "text-white hover:bg-white/10 font-medium"
              )
            }
          >
            <MessageSquareWarning className={cn("shrink-0", collapsed ? "size-4" : "size-3.5")} />
            {!collapsed && (
              <span className="whitespace-nowrap overflow-hidden text-ellipsis">
                Complaints
              </span>
            )}
          </NavLink>
          </CollapsedTooltip>
        )}

        {/* Contacts (Direct Link) */}
        {canReadContacts && (
          <CollapsedTooltip label={"Contacts"} active={collapsed}>
            <NavLink
            to="/contacts"
            className={({ isActive }) =>
              cn(
                "flex items-center rounded-lg text-xs font-medium transition-all group relative",
                collapsed
                  ? "justify-center size-9 mx-auto"
                  : "gap-3 px-3 py-2.5",
                isActive
                  ? "bg-white/20 text-white shadow-xs font-semibold"
                  : "text-white hover:bg-white/10 font-medium"
              )
            }
          >
            <Contact className={cn("shrink-0", collapsed ? "size-4" : "size-3.5")} />
            {!collapsed && (
              <span className="whitespace-nowrap overflow-hidden text-ellipsis">
                Contacts
              </span>
            )}
          </NavLink>
          </CollapsedTooltip>
        )}

        {/* Agents (Direct Link) */}
        {canReadAgents && (
          <CollapsedTooltip label={"Agents"} active={collapsed}>
            <NavLink
            to="/agents"
            className={({ isActive }) =>
              cn(
                "flex items-center rounded-lg text-xs font-medium transition-all group relative",
                collapsed
                  ? "justify-center size-9 mx-auto"
                  : "gap-3 px-3 py-2.5",
                isActive
                  ? "bg-white/20 text-white shadow-xs font-semibold"
                  : "text-white hover:bg-white/10 font-medium"
              )
            }
          >
            <Handshake className={cn("shrink-0", collapsed ? "size-4" : "size-3.5")} />
            {!collapsed && (
              <span className="whitespace-nowrap overflow-hidden text-ellipsis">
                Agents
              </span>
            )}
          </NavLink>
          </CollapsedTooltip>
        )}
        </NavSection>

        <NavSection label="Catalog" collapsed={collapsed} show={canReadProducts}>
        {/* Products (Direct Link) */}
        {canReadProducts && (
          <CollapsedTooltip label={"Products"} active={collapsed}>
            <NavLink
            to="/products"
            className={({ isActive }) =>
              cn(
                "flex items-center rounded-lg text-xs font-medium transition-all group relative",
                collapsed
                  ? "justify-center size-9 mx-auto"
                  : "gap-3 px-3 py-2.5",
                isActive
                  ? "bg-white/20 text-white shadow-xs font-semibold"
                  : "text-white hover:bg-white/10 font-medium"
              )
            }
          >
            <Package className={cn("shrink-0", collapsed ? "size-4" : "size-3.5")} />
            {!collapsed && (
              <span className="whitespace-nowrap overflow-hidden text-ellipsis">
                Products
              </span>
            )}
          </NavLink>
          </CollapsedTooltip>
        )}
        </NavSection>

        <NavSection label="Inventory" collapsed={collapsed} show={canReadInventory}>
        {[
          { to: "/inventory", label: "Stock", icon: Boxes, end: true },
          { to: "/inventory/receipts", label: "Goods Receipts", icon: PackagePlus, end: false },
          { to: "/suppliers", label: "Suppliers", icon: Truck, end: false },
          { to: "/inventory/warehouses", label: "Warehouses", icon: Warehouse, end: false },
          { to: "/inventory/quality", label: "Quality", icon: ClipboardCheck, end: false },
          { to: "/inventory/reports", label: "Stock Reports", icon: BarChart3, end: false },
        ].map((item) => (
          <CollapsedTooltip key={item.to} label={item.label} active={collapsed}>
            <NavLink
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  "flex items-center rounded-lg text-xs font-medium transition-all group relative",
                  collapsed ? "justify-center size-9 mx-auto" : "gap-3 px-3 py-2.5",
                  isActive ? "bg-white/20 text-white shadow-xs font-semibold" : "text-white hover:bg-white/10 font-medium"
                )
              }
            >
              <item.icon className={cn("shrink-0", collapsed ? "size-4" : "size-3.5")} />
              {!collapsed && <span className="whitespace-nowrap overflow-hidden text-ellipsis">{item.label}</span>}
            </NavLink>
          </CollapsedTooltip>
        ))}
        </NavSection>

        <NavSection label="Production" collapsed={collapsed} show={canReadProduction}>
        {[
          { to: "/production", label: "Work Orders", icon: Factory, end: false },
          { to: "/settings/processes", label: "Processes", icon: Workflow, end: false },
        ].map((item) => (
          <CollapsedTooltip key={item.to} label={item.label} active={collapsed}>
            <NavLink
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  "flex items-center rounded-lg text-xs font-medium transition-all group relative",
                  collapsed ? "justify-center size-9 mx-auto" : "gap-3 px-3 py-2.5",
                  isActive ? "bg-white/20 text-white shadow-xs font-semibold" : "text-white hover:bg-white/10 font-medium"
                )
              }
            >
              <item.icon className={cn("shrink-0", collapsed ? "size-4" : "size-3.5")} />
              {!collapsed && <span className="whitespace-nowrap overflow-hidden text-ellipsis">{item.label}</span>}
            </NavLink>
          </CollapsedTooltip>
        ))}
        </NavSection>

        <NavSection label="Insights" collapsed={collapsed} show={canViewReports || canReadTargets}>
        {/* Reports (Direct Link) */}
        {canViewReports && (
          <CollapsedTooltip label={"Reports"} active={collapsed}>
            <NavLink
            to="/reports"
            className={({ isActive }) =>
              cn(
                "flex items-center rounded-lg text-xs font-medium transition-all group relative",
                collapsed
                  ? "justify-center size-9 mx-auto"
                  : "gap-3 px-3 py-2.5",
                isActive
                  ? "bg-white/20 text-white shadow-xs font-semibold"
                  : "text-white hover:bg-white/10 font-medium"
              )
            }
          >
            <BarChart3 className={cn("shrink-0", collapsed ? "size-4" : "size-3.5")} />
            {!collapsed && (
              <span className="whitespace-nowrap overflow-hidden text-ellipsis">
                Reports
              </span>
            )}
          </NavLink>
          </CollapsedTooltip>
        )}

        {/* Targets (Direct Link) */}
        {canReadTargets && (
          <CollapsedTooltip label={"Targets"} active={collapsed}>
            <NavLink
            to="/targets"
            className={({ isActive }) =>
              cn(
                "flex items-center rounded-lg text-xs font-medium transition-all group relative",
                collapsed
                  ? "justify-center size-9 mx-auto"
                  : "gap-3 px-3 py-2.5",
                isActive
                  ? "bg-white/20 text-white shadow-xs font-semibold"
                  : "text-white hover:bg-white/10 font-medium"
              )
            }
          >
            <Target className={cn("shrink-0", collapsed ? "size-4" : "size-3.5")} />
            {!collapsed && (
              <span className="whitespace-nowrap overflow-hidden text-ellipsis">
                Targets
              </span>
            )}
          </NavLink>
          </CollapsedTooltip>
        )}
        </NavSection>

        <NavSection label="Administration" collapsed={collapsed} show={canSeeUserManagement || isSuperAdmin}>
        {/* 3. User Management Submenu */}
        {canSeeUserManagement && (
          collapsed ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  title="User Management"
                  className={cn(
                    "flex items-center justify-center size-9 mx-auto rounded-lg transition-all group relative",
                    isUsersActive
                      ? "bg-white/20 text-white shadow-xs font-semibold"
                      : "text-white hover:bg-white/10 font-medium"
                  )}
                >
                  <Users className="size-5 shrink-0" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                side="right"
                align="start"
                sideOffset={14}
                className="w-48 bg-sidebar border-sidebar-border text-sidebar-foreground p-1.5 shadow-2xl rounded-xl z-50"
              >
                <DropdownMenuLabel className="text-[11px] font-bold uppercase tracking-wider text-white/80 px-2.5 py-1.5">
                  User Management
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-slate-700/60 my-1" />
                {canManageUsers && (
                  <DropdownMenuItem asChild>
                    <NavLink
                      to="/users"
                      className={({ isActive }) =>
                        cn(
                          "flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs font-medium transition-colors cursor-pointer w-full",
                          isActive
                            ? "bg-white/20 text-white shadow-xs font-semibold"
                            : "text-white hover:bg-white/10 font-medium"
                        )
                      }
                    >
                      <Users className="size-4 shrink-0" />
                      <span>Users</span>
                    </NavLink>
                  </DropdownMenuItem>
                )}
                {canManageRoles && (
                  <DropdownMenuItem asChild>
                    <NavLink
                      to="/roles"
                      className={({ isActive }) =>
                        cn(
                          "flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs font-medium transition-colors cursor-pointer w-full",
                          isActive
                            ? "bg-white/20 text-white shadow-xs font-semibold"
                            : "text-white hover:bg-white/10 font-medium"
                        )
                      }
                    >
                      <ShieldCheck className="size-4 shrink-0" />
                      <span>User Roles</span>
                    </NavLink>
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="space-y-1">
              <button
                type="button"
                onClick={() => toggleMenu("users")}
                className={cn(
                  "w-full flex items-center justify-between rounded-lg px-2.5 py-1.5 text-xs font-medium transition-all group",
                  isUsersActive
                    ? "text-white font-semibold bg-white/15"
                    : "text-white hover:bg-white/10 font-medium"
                )}
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  <Users
                    className={cn(
                      "size-4 shrink-0 transition-colors",
                      isUsersActive
                        ? "text-white"
                        : "text-white"
                    )}
                  />
                  <span className="whitespace-nowrap overflow-hidden text-ellipsis">
                    User Management
                  </span>
                </div>
                <ChevronDown
                  className={cn(
                    "size-4 shrink-0 text-white transition-transform duration-200",
                    openMenu !== "users" && "-rotate-90"
                  )}
                />
              </button>

              {openMenu === "users" && (
                <div className="pl-3 pr-1 py-1 space-y-1 ml-4 border-l border-slate-700/60">
                  {canManageUsers && (
                    <NavLink
                      to="/users"
                      className={({ isActive }) =>
                        cn(
                          "flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs font-medium transition-all group",
                          isActive
                            ? "bg-white/20 text-white shadow-xs font-semibold"
                            : "text-white hover:bg-white/10 font-medium"
                        )
                      }
                    >
                      <Users className="size-3.5 shrink-0 opacity-70 group-hover:opacity-100" />
                      <span className="whitespace-nowrap overflow-hidden text-ellipsis">
                        Users
                      </span>
                    </NavLink>
                  )}

                  {canManageRoles && (
                    <NavLink
                      to="/roles"
                      className={({ isActive }) =>
                        cn(
                          "flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs font-medium transition-all group",
                          isActive
                            ? "bg-white/20 text-white shadow-xs font-semibold"
                            : "text-white hover:bg-white/10 font-medium"
                        )
                      }
                    >
                      <ShieldCheck className="size-3.5 shrink-0 opacity-70 group-hover:opacity-100" />
                      <span className="whitespace-nowrap overflow-hidden text-ellipsis">
                        User Roles
                      </span>
                    </NavLink>
                  )}
                </div>
              )}
            </div>
          )
        )}

        {/* Audit Log (Super Admin) */}
        {isSuperAdmin && (
          <CollapsedTooltip label={"Audit Log"} active={collapsed}>
            <NavLink
            to="/audit-log"
            className={({ isActive }) =>
              cn(
                "flex items-center rounded-lg text-xs font-medium transition-all group relative",
                collapsed
                  ? "justify-center size-9 mx-auto"
                  : "gap-3 px-3 py-2.5",
                isActive
                  ? "bg-white/20 text-white shadow-xs font-semibold"
                  : "text-white hover:bg-white/10 font-medium"
              )
            }
          >
            <ShieldCheck className={cn("shrink-0", collapsed ? "size-4" : "size-3.5")} />
            {!collapsed && (
              <span className="whitespace-nowrap overflow-hidden text-ellipsis">
                Audit Log
              </span>
            )}
          </NavLink>
          </CollapsedTooltip>
        )}
        </NavSection>

        <NavSection label="Settings" collapsed={collapsed} show={canSeeSettings}>
        {/* 3. Settings Submenu */}
        {canSeeSettings && (
          collapsed ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  title="Settings"
                  className={cn(
                    "flex items-center justify-center size-9 mx-auto rounded-lg transition-all group relative",
                    isSettingsActive
                      ? "bg-white/20 text-white shadow-xs font-semibold"
                      : "text-white hover:bg-white/10 font-medium"
                  )}
                >
                  <Settings className="size-5 shrink-0" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                side="right"
                align="start"
                sideOffset={14}
                className="w-48 bg-sidebar border-sidebar-border text-sidebar-foreground p-1.5 shadow-2xl rounded-xl z-50"
              >
                <DropdownMenuLabel className="text-[11px] font-bold uppercase tracking-wider text-white/80 px-2.5 py-1.5">
                  Settings
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-slate-700/60 my-1" />
                {canManageSettings && (
                  <>
                    <DropdownMenuItem asChild>
                      <NavLink
                        to="/settings/company"
                        className={({ isActive }) =>
                          cn(
                            "flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs font-medium transition-colors cursor-pointer w-full",
                            isActive
                              ? "bg-white/20 text-white shadow-xs font-semibold"
                              : "text-white hover:bg-white/10 font-medium"
                          )
                        }
                      >
                        <Building2 className="size-4 shrink-0" />
                        <span>Company</span>
                      </NavLink>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <NavLink
                        to="/settings/currencies"
                        className={({ isActive }) =>
                          cn(
                            "flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs font-medium transition-colors cursor-pointer w-full",
                            isActive
                              ? "bg-white/20 text-white shadow-xs font-semibold"
                              : "text-white hover:bg-white/10 font-medium"
                          )
                        }
                      >
                        <Coins className="size-4 shrink-0" />
                        <span>Currencies</span>
                      </NavLink>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <NavLink
                        to="/settings/countries"
                        className={({ isActive }) =>
                          cn(
                            "flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs font-medium transition-colors cursor-pointer w-full",
                            isActive
                              ? "bg-white/20 text-white shadow-xs font-semibold"
                              : "text-white hover:bg-white/10 font-medium"
                          )
                        }
                      >
                        <Globe2 className="size-4 shrink-0" />
                        <span>Countries</span>
                      </NavLink>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <NavLink
                        to="/settings/pipelines"
                        className={({ isActive }) =>
                          cn(
                            "flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs font-medium transition-colors cursor-pointer w-full",
                            isActive
                              ? "bg-white/20 text-white shadow-xs font-semibold"
                              : "text-white hover:bg-white/10 font-medium"
                          )
                        }
                      >
                        <GitBranch className="size-4 shrink-0" />
                        <span>Pipelines</span>
                      </NavLink>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <NavLink
                        to="/settings/rules"
                        className={({ isActive }) =>
                          cn(
                            "flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs font-medium transition-colors cursor-pointer w-full",
                            isActive
                              ? "bg-white/20 text-white shadow-xs font-semibold"
                              : "text-white hover:bg-white/10 font-medium"
                          )
                        }
                      >
                        <SlidersHorizontal className="size-4 shrink-0" />
                        <span>Business Rules</span>
                      </NavLink>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <NavLink
                        to="/settings/masters"
                        className={({ isActive }) =>
                          cn(
                            "flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs font-medium transition-colors cursor-pointer w-full",
                            isActive
                              ? "bg-white/20 text-white shadow-xs font-semibold"
                              : "text-white hover:bg-white/10 font-medium"
                          )
                        }
                      >
                        <Sliders className="size-4 shrink-0" />
                        <span>Master Lookups</span>
                      </NavLink>
                    </DropdownMenuItem>
                  </>
                )}
                {canSeeWhatsAppSettings && (
                  <DropdownMenuItem asChild>
                    <NavLink
                      to="/settings/whatsapp"
                      className={({ isActive }) =>
                        cn(
                          "flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs font-medium transition-colors cursor-pointer w-full",
                          isActive
                            ? "bg-white/20 text-white shadow-xs font-semibold"
                            : "text-white hover:bg-white/10 font-medium"
                        )
                      }
                    >
                      <WhatsAppIcon color="#ffffff" className="size-4 shrink-0" />
                      <span>WhatsApp</span>
                    </NavLink>
                  </DropdownMenuItem>
                )}
                {canManageLeadSources && (
                  <DropdownMenuItem asChild>
                    <NavLink
                      to="/settings/lead-sources"
                      className={({ isActive }) =>
                        cn(
                          "flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs font-medium transition-colors cursor-pointer w-full",
                          isActive
                            ? "bg-white/20 text-white shadow-xs font-semibold"
                            : "text-white hover:bg-white/10 font-medium"
                        )
                      }
                    >
                      <Radio className="size-4 shrink-0" />
                      <span>Lead Sources</span>
                    </NavLink>
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="space-y-1">
              <button
                type="button"
                onClick={() => toggleMenu("settings")}
                className={cn(
                  "w-full flex items-center justify-between rounded-lg px-2.5 py-1.5 text-xs font-medium transition-all group",
                  isSettingsActive
                    ? "text-white font-semibold bg-white/15"
                    : "text-white hover:bg-white/10 font-medium"
                )}
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  <Settings
                    className={cn(
                      "size-4 shrink-0 transition-colors",
                      isSettingsActive
                        ? "text-white"
                        : "text-white"
                    )}
                  />
                  <span className="whitespace-nowrap overflow-hidden text-ellipsis">
                    Settings
                  </span>
                </div>
                <ChevronDown
                  className={cn(
                    "size-4 shrink-0 text-white transition-transform duration-200",
                    openMenu !== "settings" && "-rotate-90"
                  )}
                />
              </button>

              {openMenu === "settings" && (
                <div className="pl-3 pr-1 py-1 space-y-1 ml-4 border-l border-slate-700/60">
                  {canManageSettings && (
                    <>
                      <NavLink
                        to="/settings/company"
                        className={({ isActive }) =>
                          cn(
                            "flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs font-medium transition-all group",
                            isActive
                              ? "bg-white/20 text-white shadow-xs font-semibold"
                              : "text-white hover:bg-white/10 font-medium"
                          )
                        }
                      >
                        <Building2 className="size-3.5 shrink-0 opacity-70 group-hover:opacity-100" />
                        <span className="whitespace-nowrap overflow-hidden text-ellipsis">
                          Company
                        </span>
                      </NavLink>

                      <NavLink
                        to="/settings/currencies"
                        className={({ isActive }) =>
                          cn(
                            "flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs font-medium transition-all group",
                            isActive
                              ? "bg-white/20 text-white shadow-xs font-semibold"
                              : "text-white hover:bg-white/10 font-medium"
                          )
                        }
                      >
                        <Coins className="size-3.5 shrink-0 opacity-70 group-hover:opacity-100" />
                        <span className="whitespace-nowrap overflow-hidden text-ellipsis">
                          Currencies
                        </span>
                      </NavLink>

                      <NavLink
                        to="/settings/countries"
                        className={({ isActive }) =>
                          cn(
                            "flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs font-medium transition-all group",
                            isActive
                              ? "bg-white/20 text-white shadow-xs font-semibold"
                              : "text-white hover:bg-white/10 font-medium"
                          )
                        }
                      >
                        <Globe2 className="size-3.5 shrink-0 opacity-70 group-hover:opacity-100" />
                        <span className="whitespace-nowrap overflow-hidden text-ellipsis">
                          Countries
                        </span>
                      </NavLink>

                      <NavLink
                        to="/settings/pipelines"
                        className={({ isActive }) =>
                          cn(
                            "flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs font-medium transition-all group",
                            isActive
                              ? "bg-white/20 text-white shadow-xs font-semibold"
                              : "text-white hover:bg-white/10 font-medium"
                          )
                        }
                      >
                        <GitBranch className="size-3.5 shrink-0 opacity-70 group-hover:opacity-100" />
                        <span className="whitespace-nowrap overflow-hidden text-ellipsis">
                          Pipelines
                        </span>
                      </NavLink>

                      <NavLink
                        to="/settings/rules"
                        className={({ isActive }) =>
                          cn(
                            "flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs font-medium transition-all group",
                            isActive
                              ? "bg-white/20 text-white shadow-xs font-semibold"
                              : "text-white hover:bg-white/10 font-medium"
                          )
                        }
                      >
                        <SlidersHorizontal className="size-3.5 shrink-0 opacity-70 group-hover:opacity-100" />
                        <span className="whitespace-nowrap overflow-hidden text-ellipsis">
                          Business Rules
                        </span>
                      </NavLink>

                      <NavLink
                        to="/settings/masters"
                        className={({ isActive }) =>
                          cn(
                            "flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs font-medium transition-all group",
                            isActive
                              ? "bg-white/20 text-white shadow-xs font-semibold"
                              : "text-white hover:bg-white/10 font-medium"
                          )
                        }
                      >
                        <Sliders className="size-3.5 shrink-0 opacity-70 group-hover:opacity-100" />
                        <span className="whitespace-nowrap overflow-hidden text-ellipsis">
                          Master Lookups
                        </span>
                      </NavLink>
                    </>
                  )}
                  {canSeeWhatsAppSettings && (
                    <NavLink
                      to="/settings/whatsapp"
                      className={({ isActive }) =>
                        cn(
                          "flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs font-medium transition-all group",
                          isActive
                            ? "bg-white/20 text-white shadow-xs font-semibold"
                            : "text-white hover:bg-white/10 font-medium"
                        )
                      }
                    >
                      <WhatsAppIcon color="#ffffff" className="size-3.5 shrink-0 opacity-70 group-hover:opacity-100" />
                      <span className="whitespace-nowrap overflow-hidden text-ellipsis">WhatsApp</span>
                    </NavLink>
                  )}
                  {canManageLeadSources && (
                    <NavLink
                      to="/settings/lead-sources"
                      className={({ isActive }) =>
                        cn(
                          "flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs font-medium transition-all group",
                          isActive
                            ? "bg-white/20 text-white shadow-xs font-semibold"
                            : "text-white hover:bg-white/10 font-medium"
                        )
                      }
                    >
                      <Radio className="size-3.5 shrink-0 opacity-70 group-hover:opacity-100" />
                      <span className="whitespace-nowrap overflow-hidden text-ellipsis">Lead Sources</span>
                    </NavLink>
                  )}
                </div>
              )}
            </div>
          )
        )}
        </NavSection>
      </div>

      {/* Footer / Toggle */}
      <div
        className={cn(
          "border-t border-slate-700/50 transition-all duration-300 p-2 flex items-center",
          collapsed ? "justify-center" : "justify-end"
        )}
      >
        {onToggle && (
          <button
            type="button"
            onClick={onToggle}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className="size-7 rounded-md flex items-center justify-center text-white hover:bg-white/15 transition-colors shrink-0 cursor-pointer"
          >
            {collapsed ? (
              <PanelLeftOpen className="size-3.5" />
            ) : (
              <PanelLeftClose className="size-3.5" />
            )}
          </button>
        )}
      </div>
    </aside>
  );
}
